import type {ComponentStageData, ComponentStageHeuristicDefinition, PluginContext} from '../../types'
import {
    BASE_HORIZONTAL_PADDING,
    CENTER_COLUMN_PINNED_RANGES,
    CENTER_ROW_PINNED_RANGES,
    CORNER_PINNED_RANGES,
    CTX_ABSOLUTE_AVAILABLE_WIDTH,
    HEADER_COMPONENTS_WIDTH,
    INSIDE_MENU_COMPONENT_WIDTH
} from './constants'
import {SemanticTypes} from '../../preprocess/conversionDataBuilders'
import {
    getMeasurementsPointer,
    getSourceLayoutPointer,
    getStructurePointer,
    getTargetLayoutPointer
} from '../../conversionDal/utils/pointer'
import {
    getParent,
    updateTargetLayout,
    predicatePinnedHeuristic,
    isPinnedToContainer,
    isHeaderFirstRowChildComponent,
    isMenuContainerChild,
    isInternalBox,
    isBoxPlugin,
    isSuperGridCell,
    isChildOfHeader,
    isSection
} from '../common'
import type {Pointer} from '@wix/document-services-types'
import {deepClone} from '@wix/wix-immutable-proxy'
import _ from 'lodash'
import {Orientation} from '../../enums'

export const getAvailableWidthKey = (componentPointer: Pointer) =>
    `${CTX_ABSOLUTE_AVAILABLE_WIDTH}-${componentPointer.id}`

const getAvailableWidth = (contextHelper: PluginContext, componentPointer: Pointer) => {
    return contextHelper.get(getAvailableWidthKey(componentPointer))
}

const getAvailableWidthForParent = (componentStageData: ComponentStageData, contextHelper: PluginContext): number => {
    const {componentPointer, conversionDal, config} = componentStageData
    const parentPointer = getParent(conversionDal, componentPointer)
    return parentPointer ? getAvailableWidth(contextHelper, parentPointer) : config.targetCanvasSize
}

const updateComponentWidth = (
    componentStageData: ComponentStageData,
    contextHelper: PluginContext,
    width: number,
    availableWidth: number = getAvailableWidthForParent(componentStageData, contextHelper)
) => {
    const {conversionDal, componentPointer} = componentStageData
    const {componentLayout} = conversionDal.get(getTargetLayoutPointer(componentPointer)) ?? {}
    contextHelper.set(getAvailableWidthKey(componentPointer), width)

    updateTargetLayout(componentStageData, {
        componentLayout: {
            ...componentLayout,
            width: {
                value: (width / availableWidth) * 100,
                type: 'percentage'
            },
            type: 'ComponentLayout'
        }
    })
}

const updateComponentBoxPluginWidth = (
    componentStageData: ComponentStageData,
    contextHelper: PluginContext,
    width: number
) => {
    const {config} = componentStageData
    updateComponentWidth(componentStageData, contextHelper, width, config.targetCanvasSize)
}

const updatePinnedHorizontalMargins = (
    componentStageData: ComponentStageData,
    target: number | undefined,
    contextHelper: PluginContext
) => {
    const {conversionDal, componentPointer} = componentStageData
    const {itemLayout} = deepClone(conversionDal.get(getSourceLayoutPointer(componentPointer)))
    const {itemLayout: targetItemLayout} = conversionDal.get(getTargetLayoutPointer(componentPointer))
    const {margins, alignSelf, justifySelf} = itemLayout
    const {left, right} = margins

    const marginToUpdate = left?.value > 0 ? left : right
    if (target) marginToUpdate.value = target

    updateTargetLayout(componentStageData, {
        itemLayout: _.merge({}, targetItemLayout, {
            alignSelf,
            justifySelf,
            margins: {
                left,
                right
            },
            type: 'FixedItemLayout'
        })
    })

    const availableWidth = getAvailableWidthForParent(componentStageData, contextHelper)
    contextHelper.set(
        getAvailableWidthKey(componentPointer),
        availableWidth - marginToUpdate.value - BASE_HORIZONTAL_PADDING
    )
}

//TODO: change this once we have a way heuristic to pinned containers
const updatePinnedToContainerHorizontalMargins = (
    componentStageData: ComponentStageData,
    target: number | undefined,
    contextHelper: PluginContext
) => {
    const {conversionDal, componentPointer} = componentStageData
    const {itemLayout} = deepClone(conversionDal.get(getSourceLayoutPointer(componentPointer)))
    const {itemLayout: targetItemLayout} = conversionDal.get(getTargetLayoutPointer(componentPointer))
    const {margins, alignSelf, justifySelf} = itemLayout
    const {left, right} = margins

    const marginToUpdate = left?.value > 0 ? left : right
    if (target) marginToUpdate.value = target

    updateTargetLayout(componentStageData, {
        itemLayout: _.merge({}, targetItemLayout, {
            alignSelf,
            justifySelf,
            margins: {
                left,
                right
            },
            type: 'GridItemLayout'
        })
    })

    const availableWidth = getAvailableWidthForParent(componentStageData, contextHelper)
    contextHelper.set(
        getAvailableWidthKey(componentPointer),
        availableWidth - marginToUpdate.value - BASE_HORIZONTAL_PADDING
    )
}

const updateHorizontalMargins = (
    componentStageData: ComponentStageData,
    contextHelper: PluginContext,
    left: number,
    availableWidth: number = getAvailableWidthForParent(componentStageData, contextHelper)
) => {
    const {conversionDal, componentPointer} = componentStageData
    const {itemLayout} = deepClone(conversionDal.get(getTargetLayoutPointer(componentPointer)))
    contextHelper.set(getAvailableWidthKey(componentPointer), availableWidth - 2 * left)

    updateTargetLayout(componentStageData, {
        itemLayout: _.merge({}, itemLayout, {
            margins: {
                left: {
                    value: (left / availableWidth) * 100,
                    type: 'percentage'
                },
                right: {
                    value: (left / availableWidth) * 100,
                    type: 'percentage'
                }
            }
        })
    })
}

const updateHorizontalMarginsForBoxPlugin = (
    componentStageData: ComponentStageData,
    contextHelper: PluginContext,
    left: number
) => {
    const {config} = componentStageData
    updateHorizontalMargins(componentStageData, contextHelper, left, config.targetCanvasSize)
}

const findIndexInHeaderFirstRow = (componentStageData: ComponentStageData) => {
    const {conversionDal, componentPointer} = componentStageData
    const component = conversionDal.get(componentPointer)
    const containerPointer = getStructurePointer({id: component.parent})
    const children = conversionDal.get(containerPointer)?.components ?? []
    const myIndex = children?.findIndex((childId: string) => childId === componentPointer.id)
    return myIndex
}

export const horizontalMarginsHeuristics: ComponentStageHeuristicDefinition[] = [
    ...CORNER_PINNED_RANGES.map(({min, max, target}) => ({
        predicate: (componentStageData: ComponentStageData) =>
            predicatePinnedHeuristic(
                componentStageData,
                Orientation.Horizontal,
                min,
                max,
                (justifySelf, alignSelf) => alignSelf !== 'center' && justifySelf !== 'center'
            ),
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            //TODO: change this once we have a way heuristic to pinned containers
            if (isPinnedToContainer(componentStageData)) {
                updatePinnedToContainerHorizontalMargins(componentStageData, target, contextHelper)
                return
            }
            updatePinnedHorizontalMargins(componentStageData, target, contextHelper)
        },
        heuristicCode: `CORNER PINNED ELEMENT ${min}-${max} -> ${target}`
    })),
    ...CENTER_COLUMN_PINNED_RANGES.map(({min, max, target}) => ({
        predicate: (componentStageData: ComponentStageData) =>
            predicatePinnedHeuristic(
                componentStageData,
                Orientation.Horizontal,
                min,
                max,
                justifySelf => justifySelf === 'center'
            ),
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            //TODO: change this once we have a way heuristic to pinned containers
            if (isPinnedToContainer(componentStageData)) {
                updatePinnedToContainerHorizontalMargins(componentStageData, target, contextHelper)
                return
            }
            updatePinnedHorizontalMargins(componentStageData, target, contextHelper)
        },
        heuristicCode: `CENTER COLUMNED PINNED ELEMENT ${min}-${max} -> ${target}`
    })),
    ...CENTER_ROW_PINNED_RANGES.map(({min, max, target}) => ({
        predicate: (componentStageData: ComponentStageData) =>
            predicatePinnedHeuristic(
                componentStageData,
                Orientation.Horizontal,
                min,
                max,
                (justifySelf, alignSelf) => alignSelf === 'center'
            ),
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            //TODO: change this once we have a way heuristic to pinned containers
            if (isPinnedToContainer(componentStageData)) {
                updatePinnedToContainerHorizontalMargins(componentStageData, target, contextHelper)
                return
            }
            updatePinnedHorizontalMargins(componentStageData, target, contextHelper)
        },
        heuristicCode: `CENTER ROWED PINNED ELEMENT ${min}-${max} -> ${target}`
    })),
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) =>
            isMenuContainerChild(componentPointer, conversionDal),
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const availableWidthForParent = getAvailableWidthForParent(componentStageData, contextHelper)
            updateHorizontalMargins(
                componentStageData,
                contextHelper,
                (availableWidthForParent - INSIDE_MENU_COMPONENT_WIDTH) / 2
            )
        },
        heuristicCode: 'MENU CONTAINER CHILD'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            const component = conversionDal.get(componentPointer)
            return (
                isHeaderFirstRowChildComponent(componentPointer, conversionDal) &&
                component.semanticType === SemanticTypes.MENU
            )
        },
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const availableWidth = getAvailableWidthForParent(componentStageData, contextHelper)
            updateHorizontalMargins(
                componentStageData,
                contextHelper,
                availableWidth - HEADER_COMPONENTS_WIDTH.MENU_WIDTH - HEADER_COMPONENTS_WIDTH.HEADER_SIDE_MARGINS
            )
        },
        heuristicCode: 'MENU IN HEADER FIRST ROW'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            const component = conversionDal.get(componentPointer)
            return (
                isHeaderFirstRowChildComponent(componentPointer, conversionDal) &&
                component.semanticType === SemanticTypes.LOGO
            )
        },
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) =>
            updateHorizontalMargins(componentStageData, contextHelper, HEADER_COMPONENTS_WIDTH.HEADER_SIDE_MARGINS),
        heuristicCode: 'LOGO IN HEADER FIRST ROW'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            return isHeaderFirstRowChildComponent(componentPointer, conversionDal)
        },
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const availableWidth = getAvailableWidthForParent(componentStageData, contextHelper)
            const myIndex = findIndexInHeaderFirstRow(componentStageData)
            updateHorizontalMargins(
                componentStageData,
                contextHelper,
                availableWidth -
                    HEADER_COMPONENTS_WIDTH.HEADER_COMPONENT_GAP * myIndex -
                    HEADER_COMPONENTS_WIDTH.COMPONENT_IN_HEADER_WIDTH * myIndex -
                    HEADER_COMPONENTS_WIDTH.MENU_WIDTH -
                    HEADER_COMPONENTS_WIDTH.HEADER_SIDE_MARGINS
            )
        },
        heuristicCode: 'COMPONENT IN HEADER FIRST ROW'
    },
    {
        predicate: isBoxPlugin,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            updateHorizontalMarginsForBoxPlugin(componentStageData, contextHelper, BASE_HORIZONTAL_PADDING)
        },
        heuristicCode: 'BOX PLUGIN'
    },
    {
        predicate: isSuperGridCell,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) =>
            updateHorizontalMargins(componentStageData, contextHelper, 0),
        heuristicCode: '0 HORIZONTAL MARGINS FOR SUPERGRID CELL'
    },
    {
        predicate: isInternalBox,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const {componentPointer} = componentStageData
            contextHelper.set(
                getAvailableWidthKey(componentPointer),
                getAvailableWidthForParent(componentStageData, contextHelper)
            )
        }
    },
    {
        predicate: (componentStageData: ComponentStageData) => isSection(componentStageData),
        apply: ({componentPointer, config}: ComponentStageData, contextHelper: PluginContext) =>
            contextHelper.set(getAvailableWidthKey(componentPointer), config.targetCanvasSize),
        heuristicCode: 'SECTION X'
    },
    {
        predicate: () => true,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) =>
            updateHorizontalMargins(componentStageData, contextHelper, BASE_HORIZONTAL_PADDING),
        heuristicCode: 'FALLBACK X'
    }
]

export const widthHeuristics: ComponentStageHeuristicDefinition[] = [
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) =>
            isMenuContainerChild(componentPointer, conversionDal),
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const {componentPointer} = componentStageData
            updateComponentWidth(componentStageData, contextHelper, getAvailableWidth(contextHelper, componentPointer))
        },
        heuristicCode: 'MENU CONTAINER CHILD'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            const component = conversionDal.get(componentPointer)
            return isChildOfHeader(componentPointer, conversionDal) && component.semanticType === SemanticTypes.MENU
        },
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) =>
            updateComponentWidth(componentStageData, contextHelper, HEADER_COMPONENTS_WIDTH.MENU_WIDTH),
        heuristicCode: 'MENU IN HEADER'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            const component = conversionDal.get(componentPointer)
            return isChildOfHeader(componentPointer, conversionDal) && component.semanticType === SemanticTypes.LOGO
        },
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) =>
            updateComponentWidth(componentStageData, contextHelper, HEADER_COMPONENTS_WIDTH.LOGO_WIDTH),
        heuristicCode: 'LOGO IN HEADER'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            return isChildOfHeader(componentPointer, conversionDal)
        },
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) =>
            updateComponentWidth(componentStageData, contextHelper, HEADER_COMPONENTS_WIDTH.COMPONENT_IN_HEADER_WIDTH),
        heuristicCode: 'COMPONENT IN HEADER'
    },
    {
        predicate: (componentStageData: ComponentStageData) => isSection(componentStageData),
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) =>
            updateComponentWidth(componentStageData, contextHelper, componentStageData.config.targetCanvasSize)
    },
    {
        predicate: isBoxPlugin,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) =>
            updateComponentBoxPluginWidth(
                componentStageData,
                contextHelper,
                getAvailableWidth(contextHelper, componentStageData.componentPointer)
            ),
        heuristicCode: 'BOX PLUGIN'
    },
    {
        predicate: isInternalBox,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) =>
            updateComponentWidth(
                componentStageData,
                contextHelper,
                getAvailableWidth(contextHelper, componentStageData.componentPointer)
            ),
        heuristicCode: 'INTERNAL BOX'
    },
    {
        predicate: ({conversionDal, componentPointer}: ComponentStageData) =>
            conversionDal.get(componentPointer).semanticType === SemanticTypes.BOX,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const {componentPointer} = componentStageData
            updateComponentWidth(componentStageData, contextHelper, getAvailableWidth(contextHelper, componentPointer))
        },
        heuristicCode: 'BOX WIDTH'
    },
    {
        predicate: ({conversionDal, componentPointer, initialSnapshot}: ComponentStageData) => {
            const originalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(componentPointer))

            return (
                conversionDal.get(componentPointer).semanticType === SemanticTypes.IMAGE &&
                originalMeasurements.width <= 200
            )
        },
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const {componentPointer, initialSnapshot} = componentStageData
            const availableWidth = getAvailableWidth(contextHelper, componentPointer)
            const originalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(componentPointer))

            updateComponentWidth(
                componentStageData,
                contextHelper,
                Math.min(originalMeasurements.width, availableWidth)
            )
        },
        heuristicCode: 'NARROW IMAGE WIDTH'
    },
    {
        predicate: ({conversionDal, componentPointer}: ComponentStageData) =>
            conversionDal.get(componentPointer).semanticType === SemanticTypes.IMAGE,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const {componentPointer} = componentStageData
            updateComponentWidth(componentStageData, contextHelper, getAvailableWidth(contextHelper, componentPointer))
        },
        heuristicCode: 'IMAGE WIDTH'
    },
    {
        predicate: ({conversionDal, componentPointer, initialSnapshot}: ComponentStageData) => {
            const originalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(componentPointer))

            return (
                conversionDal.get(componentPointer).semanticType === SemanticTypes.SVG &&
                originalMeasurements.width <= 100
            )
        },
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const {componentPointer, initialSnapshot} = componentStageData
            const availableWidth = getAvailableWidth(contextHelper, componentPointer)
            const originalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(componentPointer))

            updateComponentWidth(
                componentStageData,
                contextHelper,
                Math.min(originalMeasurements.width, availableWidth)
            )
        },
        heuristicCode: 'NARROW SVG WIDTH'
    },
    {
        predicate: ({conversionDal, componentPointer}: ComponentStageData) =>
            conversionDal.get(componentPointer).semanticType === SemanticTypes.SVG,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const {componentPointer, initialSnapshot} = componentStageData
            const availableWidth = getAvailableWidth(contextHelper, componentPointer)
            const originalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(componentPointer))

            updateComponentWidth(
                componentStageData,
                contextHelper,
                Math.min(originalMeasurements.width * 0.7, availableWidth)
            )
        },
        heuristicCode: 'SVG WIDTH'
    },
    {
        predicate: ({conversionDal, componentPointer}: ComponentStageData) =>
            conversionDal.get(componentPointer).semanticType === SemanticTypes.TEXT,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const {componentPointer} = componentStageData
            updateComponentWidth(componentStageData, contextHelper, getAvailableWidth(contextHelper, componentPointer))
        },
        heuristicCode: 'TEXT WIDTH'
    },
    {
        predicate: (componentStageData: ComponentStageData) => {
            const {initialSnapshot, conversionDal, componentPointer} = componentStageData
            const originalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(componentPointer))

            return (
                conversionDal.get(componentPointer).semanticType === SemanticTypes.HORIZONTAL_LINE &&
                originalMeasurements.width <= 200
            )
        },
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const {initialSnapshot, componentPointer} = componentStageData
            const availableWidth = getAvailableWidth(contextHelper, componentPointer)
            const originalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(componentPointer))

            updateComponentWidth(
                componentStageData,
                contextHelper,
                Math.min(originalMeasurements.width, availableWidth)
            )
        },
        heuristicCode: 'NARROW HORIZONTAL LINE WIDTH'
    },
    {
        predicate: ({conversionDal, componentPointer}: ComponentStageData) =>
            conversionDal.get(componentPointer).semanticType === SemanticTypes.HORIZONTAL_LINE,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const {componentPointer} = componentStageData
            updateComponentWidth(componentStageData, contextHelper, getAvailableWidth(contextHelper, componentPointer))
        },
        heuristicCode: 'HORIZONTAL LINE WIDTH'
    },
    {
        predicate: ({conversionDal, componentPointer}: ComponentStageData) =>
            conversionDal.get(componentPointer).semanticType === SemanticTypes.BUTTON,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const {componentPointer, initialSnapshot} = componentStageData
            const availableWidth = getAvailableWidth(contextHelper, componentPointer)
            const originalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(componentPointer))
            updateComponentWidth(
                componentStageData,
                contextHelper,
                Math.min(originalMeasurements.width, availableWidth)
            )
        },
        heuristicCode: 'BUTTON WIDTH'
    },
    {
        predicate: () => true,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const {componentPointer} = componentStageData
            updateComponentWidth(componentStageData, contextHelper, getAvailableWidth(contextHelper, componentPointer))
        },
        heuristicCode: 'FALLBACK WIDTH'
    }
]
